import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { Button } from './ui/button';
import { savedJobsAPI } from '../api/api';
import { toast } from '../hooks/use-toast';

const SaveJobButton = ({ jobId, initialSaved = false, size = 'default', showText = false, onToggle, className = '' }) => {
  const [isSaved, setIsSaved] = useState(initialSaved);
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
    
    // Check saved status if logged in and no initial value provided
    if (token && !initialSaved) {
      checkSavedStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [jobId]);

  const checkSavedStatus = async () => {
    try {
      const response = await savedJobsAPI.checkSavedStatus(jobId);
      if (response.data.success) {
        setIsSaved(response.data.is_saved);
      }
    } catch (error) {
      // Silently fail - user might not be logged in
    }
  };

  const handleToggleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isLoggedIn) {
      toast({
        title: "Login Required",
        description: "Please log in to save jobs",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      if (isSaved) {
        await savedJobsAPI.unsaveJob(jobId);
        setIsSaved(false);
        toast({
          title: "Job Removed",
          description: "Job removed from your saved list"
        });
      } else {
        await savedJobsAPI.saveJob(jobId);
        setIsSaved(true);
        toast({
          title: "Job Saved",
          description: "Job added to your saved list"
        });
      }
      
      if (onToggle) {
        onToggle(!isSaved);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to update saved status";
      toast({
        title: "Error",
        description: message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = {
    sm: { button: 'p-1.5', icon: 'h-4 w-4' },
    default: { button: 'p-2', icon: 'h-5 w-5' },
    lg: { button: 'w-full py-6 text-lg', icon: 'h-5 w-5' }
  };
  
  const { button: buttonPadding, icon: iconSize } = sizeClasses[size] || sizeClasses.default;

  return (
    <Button
      variant="outline"
      className={`${buttonPadding} ${
        isSaved 
          ? 'bg-red-50 border-red-200 text-red-500 hover:bg-red-100' 
          : 'hover:bg-gray-100 hover:text-red-500'
      } transition-all ${className}`}
      onClick={handleToggleSave}
      disabled={loading}
      data-testid={`save-job-${jobId}`}
      title={isSaved ? "Remove from saved" : "Save job"}
    >
      <Heart 
        className={`${iconSize} ${isSaved ? 'fill-red-500 text-red-500' : ''} ${loading ? 'animate-pulse' : ''}`} 
      />
      {showText && (
        <span className="ml-2">{isSaved ? 'Saved' : 'Save Job'}</span>
      )}
    </Button>
  );
};

export default SaveJobButton;
